import { Link } from 'react-router-dom';
import { useAdminOverview } from '../useAdmin';
import './AdminOverview.css';

/**
 * Admin home (F2). One-screen dashboard of the counts that matter.
 * Each tile links to its detail page so the operator can drill into
 * any number that looks off. Auto-refreshes every 30s via the
 * `useAdminOverview` hook so an open dashboard reflects fresh queue
 * depths without a manual reload.
 */
export function AdminOverview(): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminOverview();

  if (isLoading) {
    return <p className="bl-admin-overview__notice">Loading dashboard…</p>;
  }
  if (isError) {
    return (
      <p className="bl-admin-overview__notice bl-admin-overview__notice--error" role="alert">
        Could not load the overview ({error instanceof Error ? error.message : 'unknown error'}).
      </p>
    );
  }
  if (!data) return <p>No data.</p>;

  const queue = data.submissions_awaiting_review;
  const pendingTone = queue === 0 ? 'ok' : queue < 10 ? 'warn' : 'alert';

  return (
    <section className="bl-admin-overview" aria-label="System overview">
      <header className="bl-admin-overview__header">
        <h2>Overview</h2>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="bl-admin-overview__refresh"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <div className="bl-admin-overview__grid">
        <Tile
          to="/admin/users"
          eyebrow="Users"
          big={data.users_total}
          sub={
            <>
              {data.users_by_role.standard} kids · {data.users_by_role.teacher} teachers ·{' '}
              {data.users_by_role.moderator} moderators · {data.users_by_role.admin} admins
              {data.users_suspended > 0 && (
                <span className="bl-admin-overview__warn">
                  {' · '}
                  {data.users_suspended} suspended
                </span>
              )}
            </>
          }
        />
        <Tile
          to="/admin/classrooms"
          eyebrow="Classrooms"
          big={data.classrooms_total}
          sub={
            <>
              {data.classrooms_paused} paused · {data.classrooms_allow_publish} allow publishing
            </>
          }
        />
        <Tile
          to="/admin/submissions"
          eyebrow="Awaiting review"
          big={queue}
          tone={pendingTone}
          sub={
            <>
              {data.submissions_by_status.awaiting_moderator} waiting for moderator ·{' '}
              {data.submissions_by_status.processing} processing
            </>
          }
        />
        <Tile
          to="/admin/submissions"
          eyebrow="All submissions"
          big={data.submissions_total}
          sub={
            <>
              {data.submissions_by_status.approved} approved · {data.submissions_by_status.rejected}{' '}
              rejected · {data.submissions_by_status.withdrawn} withdrawn
            </>
          }
        />
        <Tile
          to="/admin/published-apps"
          eyebrow="Published apps"
          big={data.published_apps_total}
          sub={<>{data.published_apps_total_views.toLocaleString()} total visits</>}
        />
        <Tile
          to="/admin/reports"
          eyebrow="Open reports"
          big={data.open_reports}
          tone={data.open_reports > 0 ? 'warn' : 'ok'}
        />
        <Tile
          to="/admin/messages"
          eyebrow="Inbox"
          big={data.open_messages ?? 0}
          tone={(data.open_messages ?? 0) > 0 ? 'warn' : 'ok'}
          sub={<>contact + feature requests</>}
        />
        <Tile
          eyebrow="Projects (private)"
          big={data.projects_total}
          sub={<>across all kids&apos; studios</>}
        />
      </div>
    </section>
  );
}

interface TileProps {
  eyebrow: string;
  big: number;
  sub?: React.ReactNode;
  to?: string;
  tone?: 'ok' | 'warn' | 'alert';
}

function Tile({ eyebrow, big, sub, to, tone }: TileProps): JSX.Element {
  const inner = (
    <>
      <p className="bl-admin-overview__eyebrow">{eyebrow}</p>
      <p className="bl-admin-overview__big">{big.toLocaleString()}</p>
      {sub && <p className="bl-admin-overview__sub">{sub}</p>}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="bl-admin-overview__tile" data-tone={tone}>
        {inner}
      </Link>
    );
  }
  return (
    <div className="bl-admin-overview__tile" data-tone={tone}>
      {inner}
    </div>
  );
}
